import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    LinearProgress,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Alert,
    Divider,
} from '@mui/material';
import {
    CheckCircle as StrengthIcon,
    Lightbulb as RecommendationIcon,
    Psychology as QualityIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { analyticsService } from '../../services/analyticsService';

interface QualityDimension {
    score: number;
    feedback: string;
}

interface QualityData {
    overallScore: number;
    summary: string;
    dimensions: {
        completeness?: QualityDimension;
        structure?: QualityDimension;
        content?: QualityDimension;
        learningObjectives?: QualityDimension;
        difficulty?: QualityDimension;
    };
    strengths: string[];
    recommendations: string[];
    qualityCheckedAt?: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    courseId: number;
    courseTitle: string;
    existingData?: QualityData | null;
    onQualityUpdated: (data: QualityData) => void;
}

const dimensionLabels: Record<string, string> = {
    completeness: 'Vollständigkeit',
    structure: 'Struktur',
    content: 'Inhalt',
    learningObjectives: 'Lernziele',
    difficulty: 'Schwierigkeitsgrad',
};

const getScoreColor = (score: number): 'success' | 'warning' | 'error' => {
    if (score >= 75) return 'success';
    if (score >= 50) return 'warning';
    return 'error';
};

const getScoreBarColor = (score: number) => {
    if (score >= 75) return '#2e7d32';
    if (score >= 50) return '#ed6c02';
    return '#d32f2f';
};

const CourseQualityDialog: React.FC<Props> = ({
    open,
    onClose,
    courseId,
    courseTitle,
    existingData,
    onQualityUpdated,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<QualityData | null>(existingData || null);

    const handleRunCheck = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await analyticsService.triggerQualityCheck(courseId);
            setData(result);
            onQualityUpdated(result);
        } catch (err: any) {
            setError(err?.message || 'Fehler bei der Qualitätsprüfung');
        } finally {
            setLoading(false);
        }
    };

    const displayData = data || existingData;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QualityIcon color="primary" />
                KI-Qualitätsprüfung: {courseTitle}
            </DialogTitle>

            <DialogContent>
                {/* Issue #197 (EU-KI-Act): Grenzen der KI-Analyse transparent machen */}
                <Alert severity="info" sx={{ mb: 2 }} data-testid="quality-ai-disclaimer">
                    Der Score basiert auf einer KI-Analyse von Metadaten und Lektionsstruktur.
                    Mediale Qualität (Videos, eingebettete Inhalte) wird NICHT bewertet.
                </Alert>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress size={48} sx={{ mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                            KI analysiert den Kurs... Dies kann bis zu 30 Sekunden dauern.
                        </Typography>
                    </Box>
                )}

                {!loading && displayData && (
                    <Box>
                        {/* Overall Score */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                <CircularProgress
                                    variant="determinate"
                                    value={displayData.overallScore}
                                    size={80}
                                    thickness={6}
                                    sx={{ color: getScoreBarColor(displayData.overallScore) }}
                                />
                                <Box
                                    sx={{
                                        top: 0, left: 0, bottom: 0, right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography variant="h6" fontWeight="bold">
                                        {displayData.overallScore}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box>
                                <Chip
                                    label={
                                        displayData.overallScore >= 75 ? 'Gute Qualität' :
                                        displayData.overallScore >= 50 ? 'Verbesserungsbedarf' :
                                        'Erheblicher Verbesserungsbedarf'
                                    }
                                    color={getScoreColor(displayData.overallScore)}
                                    sx={{ mb: 0.5 }}
                                />
                                {displayData.summary && (
                                    <Typography variant="body2" color="text.secondary">
                                        {displayData.summary}
                                    </Typography>
                                )}
                                {displayData.qualityCheckedAt && (
                                    <Typography variant="caption" color="text.secondary">
                                        Geprüft am {new Date(displayData.qualityCheckedAt).toLocaleString('de-DE')}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* Dimensions */}
                        {Object.keys(dimensionLabels).length > 0 && displayData.dimensions && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                                    Bewertungsdimensionen
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {Object.entries(dimensionLabels).map(([key, label]) => {
                                        const dim = displayData.dimensions[key as keyof typeof displayData.dimensions];
                                        if (!dim) return null;
                                        return (
                                            <Box key={key}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {label}
                                                    </Typography>
                                                    <Typography variant="body2" color={getScoreBarColor(dim.score)}>
                                                        {dim.score}/100
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={dim.score}
                                                    sx={{
                                                        height: 8,
                                                        borderRadius: 4,
                                                        mb: 0.5,
                                                        '& .MuiLinearProgress-bar': {
                                                            backgroundColor: getScoreBarColor(dim.score),
                                                        },
                                                    }}
                                                />
                                                {dim.feedback && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {dim.feedback}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        )}

                        {/* Strengths */}
                        {displayData.strengths?.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                                    Stärken
                                </Typography>
                                <List dense disablePadding>
                                    {displayData.strengths.map((s, i) => (
                                        <ListItem key={i} disablePadding sx={{ mb: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <StrengthIcon fontSize="small" color="success" />
                                            </ListItemIcon>
                                            <ListItemText primary={s} primaryTypographyProps={{ variant: 'body2' }} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}

                        {/* Recommendations */}
                        {displayData.recommendations?.length > 0 && (
                            <Box>
                                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                                    Empfehlungen
                                </Typography>
                                <List dense disablePadding>
                                    {displayData.recommendations.map((r, i) => (
                                        <ListItem key={i} disablePadding sx={{ mb: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <RecommendationIcon fontSize="small" color="warning" />
                                            </ListItemIcon>
                                            <ListItemText primary={r} primaryTypographyProps={{ variant: 'body2' }} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Box>
                )}

                {!loading && !displayData && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <QualityIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                            Noch keine Qualitätsprüfung durchgeführt.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Klicken Sie auf „Jetzt prüfen", um eine KI-Analyse des Kurses zu starten.
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Schließen</Button>
                <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                    onClick={handleRunCheck}
                    disabled={loading}
                >
                    {displayData ? 'Erneut prüfen' : 'Jetzt prüfen'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CourseQualityDialog;
